/**
 * A QR CODE, DRAWN HERE FROM A STRING (ops #46). The pay page shows the business's Zelle code on a
 * wide screen, for a payer to scan from the Zelle screen of her bank's app on her phone, and the code
 * is drawn from the one stored URL (`ZELLE_QR_URL` in `packages/shared/src/zelle.ts`) rather than
 * kept as an image: no file is stored or served, and the day the owner hands over a new code the one
 * string moves and the drawing with it.
 *
 * The encoder is `qrcode-generator` (Kazuhiko Arase, MIT, no dependencies), bundled into the page by
 * the build, so nothing is fetched to draw it and no script from anywhere else runs. It gives the
 * modules; this draws them as one SVG path, a run of dark modules in a row to one rectangle, black
 * on white with the four-module quiet zone the standard asks for, so a camera reads it at any size.
 * `web/src/screens/PublicPage.test.tsx` reads the drawn path back with a decoder and holds it to the
 * URL.
 */
import { useMemo } from 'react';
import qrcode from 'qrcode-generator';

/** The white margin a scanner needs around the code, in modules. The standard asks for four. */
export const QUIET_ZONE = 4;

/**
 * The code's modules for this text, row by row, true where dark. Error correction M, which lets a
 * camera recover about a sixth of a code smudged or glared on a screen. The text goes as its UTF-8
 * bytes: the encoder takes one byte per character code, so the bytes are handed to it one to a
 * character, and a character outside ASCII is encoded as itself rather than cut to its low byte.
 */
export function qrModules(text: string): boolean[][] {
  const code = qrcode(0, 'M');
  code.addData(String.fromCharCode(...new TextEncoder().encode(text)), 'Byte');
  code.make();
  const size = code.getModuleCount();
  return Array.from({ length: size }, (_, row) =>
    Array.from({ length: size }, (_, column) => code.isDark(row, column)),
  );
}

/** The dark modules as one path, a rectangle to each run in a row, placed inside the quiet zone. */
export function qrPath(modules: readonly (readonly boolean[])[]): string {
  const runs: string[] = [];
  modules.forEach((row, y) => {
    for (let x = 0; x < row.length; x += 1) {
      if (!row[x]) continue;
      let run = 1;
      while (row[x + run]) run += 1;
      runs.push(`M${x + QUIET_ZONE} ${y + QUIET_ZONE}h${run}v1h-${run}z`);
      x += run - 1;
    }
  });
  return runs.join('');
}

export function QrCode({ text, label }: { text: string; label: string }) {
  const modules = useMemo(() => qrModules(text), [text]);
  const extent = modules.length + 2 * QUIET_ZONE;
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${extent} ${extent}`}
      shapeRendering="crispEdges"
      className="mx-auto block aspect-square w-full max-w-64"
    >
      <rect width={extent} height={extent} fill="#ffffff" />
      <path d={qrPath(modules)} fill="#000000" />
    </svg>
  );
}
